import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux'

import SaveButton from '../components/psc/lib/SaveButton'
import ErrorMessage from '../components/psc/lib/ErrorMessage'
import DeleteButton from '../components/psc/lib/DeleteButton'
import CancelButton from '../components/psc/lib/CancelButton'
import { RadioGroup } from '../components/psc/lib/Radio'
import SingleSelect from '../components/psc/lib/SingleSelect'
import PickDate from '../components/psc/lib/PickDate'
import TextArea from '../components/psc/lib/TextArea'
import UserSelect from '../components/psc/lib/UserSelect'
import Loading from '../components/psc/lib/Loading'
import NoAccess from '../components/psc/lib/NoAccess'

import useAuthentication from '../hooks/useAuthentication';
import useUserManagement from '../hooks/useUserManagement';
import useProjectContents from '../hooks/useProjectContents';
import useBrowserTabTitle from '../hooks/useBrowserTabTitle';

const ActionItem = () => {
    const pscState = useSelector(state => state.pscState.value);
    const project = pscState.project;
    const params = useParams();
    const isNew = params.objID === 'new';
    const projectID = Number(params.projectID);
    const objID = Number(params.objID);

    const { setBrowserTabTitle } = useBrowserTabTitle()
    
    const { currentUser } = useAuthentication();
    const { userHasProjectAccessLevel } = useUserManagement();

    const {
        updateActionItem,
        deleteActionItem,
    } = useProjectContents()
    
    const [actionItemSources, setActionItemSources] = useState([])
    const [type, setType] = useState('External')
    const [title, setTitle] = useState('')
    const [status, setStatus] = useState('Open')
    const [raisedDate, setRaisedDate] = useState(new Date())
    const [origin, setOrigin] = useState('')
    const [description, setDescription] = useState('')
    const [assignedTo, setAssignedTo] = useState(currentUser.id)
    const [actionItemSourceID, setActionItemSourceID] = useState(0)
    const [closeReference, setCloseReference] = useState('')
    const [comments, setComments] = useState('')
    const [ownerUserID, setOwnerUserID] = useState(0)
    const [ownerFreetext, setOwnerFreetext] = useState('')
    const [dueDate, setDueDate] = useState(null)
    const [dueDateFreetext, setDueDateFreetext] = useState('')
    const [actionItemNumber, setActionItemNumber] = useState(null)
    const [updatedAt, setUpdatedAt] = useState(null)

    const [submitActive, setSubmitActive] = useState(false)
    const [submitSpinner, setSubmitSpinner] = useState(false)
    const [error, setError] = useState([])
    const [isUsed, setIsUsed] = useState(false)
    const [hasLoaded, setHasLoaded] = useState(isNew ? true : false)
    const [hasAccess, setHasAccess] = useState(true)

    const typeOptions = [
                {
                    value: 'External',
                    label: 'External',
                },
                {
                    value: 'Internal',
                    label: 'Internal',
                },
            ]
    const statusOptions = [
                {
                    value: 'Open',
                    label: 'Open',
                },
                {
                    value: 'Closed',
                    label: 'Closed',
                }
            ];

    useEffect(() => {
        fetch('/v0/action_item_sources?project_id='+projectID, { credentials: 'same-origin' })
        .then(function(response) {
            return response.json()
        })
        .then(function(json) {
            setActionItemSources(json.results)
        })
    }, [])

    useEffect(() => {
        if (isNew) {
            return
        }

        fetchObject()
    }, [objID, isNew])

    useEffect(() => {
        if( isNew ) {
            setBrowserTabTitle('New Action Item')
            return
        }
        if( title ) {
            setBrowserTabTitle(title)
        }
    }, [isNew, title, setBrowserTabTitle])

    useEffect(() => {
        let value = parseInt(actionItemSourceID, 10)
        try {
            let source = actionItemSources.find(function (s) { return s.id === value })
            if( source ) {
                setType(source.default_item_type)
            }
        } catch (e) {
            console.log('Error', e)
        }
    }, [actionItemSourceID])

    const fetchObject = () => {
        fetch('/v0/action_items/'+objID, { credentials: 'same-origin' })
            .then(function(response) {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(function(json) {
                if (json.error || json.result.id === 0) {
                    throw new Error(json.error);
                }
                
                json.result.project_id = projectID
                
                if( ! parseInt(json.result.action_item_source_id, 10) > 0 ) {
                    json.result.action_item_source_id = parseInt(localStorage.defaultActionItemSourceID, 10) || 0
                }
                setActionItemSourceID(json.result.action_item_source_id)
                setTitle(json.result.title)
                setType(json.result.type)
                setRaisedDate(new Date(json.result.raised_date))
                setDescription(json.result.description)
                setAssignedTo(json.result.assigned_to)
                setStatus(json.result.status)
                setDueDate(json.result.due_date)
                setOwnerUserID(json.result.owner_user_id)
                setOwnerFreetext(json.result.owner_freetext)
                setDueDateFreetext(json.result.due_date_freetext)
                setComments(json.result.comments)
                setCloseReference(json.result.close_reference)
                setActionItemNumber(json.result.action_item_number)
                setOrigin(json.result.origin)
                setIsUsed(json.result.is_used)
                setUpdatedAt(json.result.updated_at)
                setHasLoaded(true)
            })
            .catch(function(error) {
                console.log('Error fetching action item:', error);
                setHasAccess(false);
            })
    }

    const toBackend = () => {
        let res = {
            updated_at: updatedAt,
            project_id: projectID,
            action_item_source_id: actionItemSourceID,
            title: title,
            type: type,
            raised_date: raisedDate,
            description: description,
            assigned_to: assignedTo,
            status: status,
            due_date: dueDate,
            owner_user_id: ownerUserID,
            owner_freetext: ownerFreetext,
            due_date_freetext: dueDateFreetext,
            comments: comments,
            close_reference: closeReference,
            origin: origin
        }
        if( actionItemNumber ) {
            res.action_item_number = actionItemNumber
        }
        return res
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setSubmitSpinner(false)
        setError([ props.message ])
    }

    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }

        if( submitActive ) {
            return null
        }

        let error = []

        let source = actionItemSources.find(function (e) { return e.id === actionItemSourceID })
        if ( ! source ) {
            error.push('Select Meeting, or create a new one')
        }

        if ( ! title ) {
            error.push('Title is required')
        }

        if ( ! raisedDate ) {
            error.push('Raised Date is required')
        }

        if ( error.length > 0 ) {
            setError(error)
            return null
        }

        setSubmitActive(true)
        setSubmitSpinner(true)

        let submit = {
            objType: 'action_items',
            objID: objID,
            data: toBackend(),
            successCallback: function(json) {
                updateActionItem(json.result)
            }
        }
        return submit
    }

    if (! actionItemSources ) {
        return <h1>Loading...</h1>
    }

    let deleteButton = []
    if ( !isNew && userHasProjectAccessLevel(currentUser, projectID, 'MEMBER') && ! isUsed ) {
        var url = '/v0/action_items/'+objID;
        deleteButton.push(
            <DeleteButton
                url={url}
                onError={handleSubmitError}
                callback={() => deleteActionItem(objID)}
            />
        )
    }

    let globalDisabled = false
    if( ! userHasProjectAccessLevel(currentUser, projectID, 'MEMBER')) {
        globalDisabled = true
    }

    let pageTitle = title ? title : '<New Action>'
    if( actionItemNumber ) {
        pageTitle = 'AI-' + String(actionItemNumber).padStart(2, '0') + ' ' + pageTitle
    }

    const newMeetingLink = '/portfolio/'+projectID+'/action_item_sources/new'
    let meetingField = (
        <div className="input-group m-b" style={{display: 'table'}}>
            <span className="input-group-btn" style={{float: 'left'}}>
                <Link type="button" className="btn btn-primary" to={newMeetingLink}>New Meeting</Link>
            </span>

            <SingleSelect
                value={actionItemSourceID}
                onChange={(source) => setActionItemSourceID(source.id)}
                options={actionItemSources}
                disabled={globalDisabled}
            />
        </div>
    )
    if( globalDisabled  ) {
        // Don't show the New Meeting button
        meetingField = (
            <SingleSelect
                value={actionItemSourceID}
                onChange={(source) => setActionItemSourceID(source.id)}
                options={actionItemSources}
                disabled={globalDisabled}
            />
        )
    }

    let requiredIndicator = '';
    if (userHasProjectAccessLevel(currentUser, projectID, 'MEMBER')) {
        requiredIndicator = (<label className="form-required-star">&nbsp;*</label>)
    }

    if (!hasAccess) {
        return <NoAccess />
    }

    if (!hasLoaded) {
        return <Loading />
    }
    
    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>Action Item: {pageTitle}</h2>
                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(e) => handleSubmit(e)} autoComplete="off">

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Meeting
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        {meetingField}
                                    </div>
                                </div>

                                { actionItemNumber &&
                                    <div className="form-group">
                                        <label className="col-sm-2 control-label">AI #</label>
                                        <div className="col-sm-8">
                                            <input className="form-control" type="text" name="action_item_number"
                                                value={actionItemNumber}
                                                disabled={true} />
                                        </div>
                                    </div>
                                }

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Title
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" name="title" value={title}
                                            onChange={(e) => setTitle(e.target.value)} required disabled={globalDisabled} />
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Raised
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <PickDate
                                            value={raisedDate}
                                            onChange={(date) => setRaisedDate(date)}
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Type
                                    </label>
                                    <div className="col-sm-8">
                                        <SingleSelect
                                            name="type"
                                            value={type}
                                            onChange={(type) => setType(type.value)}
                                            options={typeOptions}
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Origin
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" name="origin" value={origin} onChange={(e) => setOrigin(e.target.value)} />
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Description</label>
                                    <div className="col-sm-8">
                                        <TextArea className="form-control" name="description" value={description} onChange={(e) => setDescription(e.target.value)} />
                                    </div>
                                </div>
                                
                                <UserSelect
                                    label="Owner"
                                    required={false}
                                    value={ownerUserID}
                                    onChange={(ownerUserID) => { setOwnerFreetext(''); setOwnerUserID(ownerUserID) }}
                                    projectId={projectID}
                                />
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" value={ownerFreetext}
                                            onChange={(e) => { setOwnerFreetext(e.target.value); setOwnerUserID(0) }}
                                            placeholder="...or type any name"
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Due
                                    </label>
                                    <div className="col-sm-8">
                                        <PickDate
                                            value={dueDate}
                                            onChange={(date) => { setDueDate(date); setDueDateFreetext('') }}
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" value={dueDateFreetext} 
                                            onChange={(e) => { setDueDateFreetext(e.target.value); setDueDate(null) }}
                                            placeholder="...or type any event or milestone"
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Status</label>
                                    <div className="col-sm-8">
                                        <RadioGroup
                                            name="status"
                                            value={status}
                                            onChange={(value) => setStatus(value)}
                                            options={statusOptions}
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Close-out Reference</label>
                                    <div className="col-sm-8">
                                        <TextArea className="form-control" name="close_reference" value={closeReference} onChange={(e) => setCloseReference(e.target.value)} />
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Comments</label>
                                    <div className="col-sm-8">
                                        <TextArea className="form-control" name="comments" value={comments} onChange={(e) => setComments(e.target.value)} />
                                    </div>
                                </div>
                                
                                <div className="hr-line-dashed"></div>
                                <div className="hr-line-dashed"></div>
                                
                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />

                                    <div className="col-sm-10 col-sm-offset-2">
                                        <CancelButton/>
                                        
                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={userHasProjectAccessLevel(currentUser,projectID, "MEMBER")}
                                            spinnerActive={submitActive}
                                            label='Save Changes'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />

                                        {deleteButton}

                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default ActionItem
